
import WDForm from './wd-form';
import { WDProxy } from '../../core/wd-proxy';
import WDApiCache from '../../core/fetch/wd-api-cache';

/**
  *	@class WDFormProxy
  */
export default class WDFormProxy extends WDForm
{

  #proxy;
  #fetch;
  #actions = [];

  constructor(cfg)
  {
    super(cfg);
    this.proxy = cfg?.proxy || new WDProxy({});

    //if user changes form value, update our proxy object with the new value
    this.on('form:change', (evt) => this.handleFormChange(evt));
  };

  get proxy() { return this.#proxy; };
  set proxy(proxy)
  {
    this.#proxy = proxy;
    this.#proxy.setRecursive(true);

    //setup an event watcher for proxy set if we have a content handler
    this.#proxy.on('set', evtData => this.handleSet(evtData));
    this.#proxy.on('delete', evtData => this.handleDelete(evtData));
  };

  /**
    * updates the proxy values when the form values change
    */
  handleFormChange(evt)
  {
    const f = evt.detail;
    const k = f.name;
    const v = f.value;

    if (f.name.includes('['))
    {
      const obj = {};
      obj[k] = v;

      const res = this.toObjectNotation(obj);
      this.#proxy.merge(res);
    }
    else
    {
      this.#proxy[k] = v;
    }
  };

  /**
    *
    */
  handleSet(evtData)
  {
    /**
      create/replace a row with this index
      */
    const key = evtData.property;
    const value = evtData.value;
    
    //handle values for full objects passed
    if (value !== null && typeof (value) == 'object')
    {
      const res = this.toFormNotation(value, key);

      for (let k in res)
      {
        const f = this.getItem(k);
        if (f && f.value != res[k]) f.value = res[k];
      }
    }
    //updating an individual member of a nested object
    else if (key.indexOf('.') != -1 && key.indexOf('_') != 0)
    {
      //convert key path dot notation to form bracket notation
      const kb = key.replace(/\.(.+?)(?=\.|$)/g, (m, s) => `[${s}]`);

      const f = this.getItem(kb);
      if (f && f.value != value) f.value = value;
    }
    //handle top-level values
    else
    {
      const f = this.getItem(key);
      if (f && f.value != value) f.value = value;
    }

  };

  handleDelete(evtData)
  {
    const key = evtData.property;

    //updating an individual member of a nested object
    if (key.indexOf('.') != -1 && key.indexOf('_') != 0)
    {
      //convert key path dot notation to form bracket notation
      const kb = key.replace(/\.(.+?)(?=\.|$)/g, (m, s) => `[${s}]`);

      const f = this.getItem(kb);
      if (f) f.value = null;
    }
    //handle top-level values
    else
    {
      const f = this.getItem(key);
      if (f) f.value = null;
    }

    //now check for any bracket-notation forms, basically forms
    //that exist for this object but below its level
    for (let k in this.items)
    {
      if (k.indexOf(key + '[') != -1)
      {
        const item = this.getItem(k);
        item.value = null;
      }
    }
  };


  addForms(cfg)
  {
    return super.addForms(cfg).then(() =>
    {
      this.triggerActions();
    });
  }

  addFormsFromURI(cfg)
  {
    return super.addFormsFromURI(cfg).then(() =>
    {
      this.triggerActions();
    });
  }

  /**
    * if the proxy is already populated, this allows us to pass the value to the form 
    * when the form is created
    */
  addForm(cfg)
  {
    if (cfg.name)
    {
      let proxyValue = null;

      if (cfg.name.indexOf('[') !== -1)
      {
        proxyValue = this.getValueFromBracketNotation(cfg.name);
      }
      else if (this.#proxy[cfg.name] !== undefined)
      {
        proxyValue = this.#proxy[cfg.name];
      }

      //we pulled the value from the proxy, overwrite whatever's in the config
      //including any default values set for the config
      if (proxyValue !== null) cfg.value = proxyValue;
    }

    //some of our forms can work directly with the proxy, so pass it as a parameter
    cfg.proxy = this.#proxy;

    //add the form
    return super.addForm(cfg).then(formItem =>
    {
      /*
      // Only setup URI template handling if fetch exists and contains templates
      if (cfg?.fetch && cfg.fetch.getReqURI().includes('${')) 
      {
        this.handleURITemplate(formItem);
      }
      // If options are already provided (pre-fetched), don't fetch again
      else if (cfg?.options && formItem.setOptions)
      {
        formItem.setOptions(cfg.options);
      }
      // If fetch exists but no options, ensure we use cached version if available
      else if (cfg?.fetch && formItem.setOptions)
      {
        // Replace regular fetch with cached version if not already cached
        if (!(cfg.fetch instanceof WDApiCache))
        {
          const originalFetch = cfg.fetch;
          cfg.fetch = new WDApiCache(originalFetch.getReqURI(), originalFetch.toObject());
          formItem.config.fetch = cfg.fetch;
        }
      }
      */
      
      return formItem;
    });

  };

  isValidFetched(uri)
  {
    return (!uri.includes('${') && 
            !uri.includes('//') && 
            !uri.includes('/undefined') && 
            !uri.includes('/null')
          );
  };

  /**
    * handle a form uri that's a template
    */
  handleURITemplate(formItem)
  {
    const uriTemplate = formItem.fetch.getReqURI();

    const watchForms = uriTemplate.getTemplateKeys();
    const newURI = uriTemplate.interpolate(this.#proxy.toObject());

    //if we could resolve the template, check if we have pre-fetched data
    if (this.isValidFetched(newURI)) 
    {
      formItem.fetchURI = newURI;
    }
   
    //our form uri has a template, setup to watch the parent forms for changes
    watchForms.forEach(watchName =>
    {
      //look for dot notation in the uri. If there is our form name will use bracket notation
      const wn = watchName.replace(/\.([^.]+)/g, (_, p1) => `[${p1}]`);
      const watchForm = this.getItem(wn);

      //found a parent form        
      if (watchForm)
      {
        watchForm.on('change', () =>
        {
          const newURI = uriTemplate.interpolate(this.#proxy.toObject());

          //if we could resolve the template, update the fetch uri
          if (this.isValidFetched(newURI)) 
          {
             formItem.fetchURI = newURI;
          }
          else 
          {
            //reset the form, the parent form has been set to something invalid
            formItem.options.replace([]);
            formItem.value = null;
          }

        });
      }

    });

  };

  getValueFromDotNotation(path)
  {
    let retVal = this.#proxy;

    path.split('.').some(k =>
    {

      retVal = retVal[k];

      //if the value for this key is undefined, return undefined
      if (retVal === undefined) return true;

    });

    return retVal;

  };

  getValueFromBracketNotation(path)
  {
    const dotPath = path.replaceAll('[', '.').replaceAll(']', '');
    return this.getValueFromDotNotation(dotPath);
  };

  /**
   * 
   * @param {*} action 
   * @param {*} srcForm 
   * @param {*} watchForm 
   * @param {*} watchVal 
   */
  addAction(action, srcForm, watchForm, watchVal)
  {
    const actionHandler = () =>
    {
      const sf = this.getItem(srcForm);
      const wf = this.getItem(watchForm);

      if (!sf) console.error('Could not find form for action',srcForm);

      if (watchVal == wf.value)
      {
        if (action == 'hide') sf.hide();
        else if (action == 'show') sf.show();
        else if (action == 'set_required') sf.required = true;
        else if (action == 'unset_required') sf.required = false;
        else if (action == 'set_readonly') sf.readonly = true;
        else if (action == 'unset_readonly') sf.readonly = false;
      }

    };

    this.#proxy.onSet(watchForm, actionHandler);
    this.#actions.push(actionHandler);
  };

  /**
   * 
   * @param {String} srcForm - name of form we are managing
   * @param {String} watchForm - name of form we are watching
   * @param {*} showVal - show the form if watchForm val matches this
   * @param {*} hideVal - hide the form if watchForm val matches this
   */
  addToggleAction(srcForm, watchForm, showVal, hideVal)
  {
    this.addAction('hide', srcForm, watchForm, hideVal);
    this.addAction('show', srcForm, watchForm, showVal);
  };

  /**
   * 
   * @param {String} srcForm - name of form we are managing
   * @param {String} watchForm - name of form we are watching
   * @param {*} reqVal - set required if watchForm val matches this value
   * @param {*} unReqVal - set not required if watchForm val matches this value
   */
  addRequiredAction(srcForm, watchForm, reqVal, unReqVal)
  {
    this.addAction('set_required', srcForm, watchForm, reqVal);
    this.addAction('unset_required', srcForm, watchForm, unReqVal);
  };

  addReadonlyAction(srcForm, watchForm, reqVal, unReqVal)
  {
    this.addAction('set_readonly', srcForm, watchForm, reqVal);
    this.addAction('unset_readonly', srcForm, watchForm, unReqVal);
  };

  triggerActions()
  {
    this.#actions.forEach( action => action() );
  };

  req() { return this.#proxy.req(); };
  post() { return this.#proxy.post(); };
  del() { return this.#proxy.del(); };

};

customElements.define('wd-form-proxy', WDFormProxy);
